import type { GameEvent, World } from '../sim/types';
import { isCleanupHome, isSurveyHome, type FinishedJob } from '../sim/operations';
import { ISLAND_NAMES } from '../sim/reef';
import { shoreDebrisT } from '../sim/shoreDebris';
import { areaFindingsHtml } from './areasPanel';
import { seeItHtml } from './onTheReef';
import { stepFromFindings } from './steer';
import { escapeHtml } from './format';
import { plainTitle } from './surveyNames';
import { CHAPTERS, SEASON, currentObjective, guideSkipped } from '../sim/campaign';
import { civilFromEpochDay, dateLabel, epochDayFromCivil, epochDayOf } from '../sim/calendar';
import { dollars, recurringPerMonth, surveyCost } from '../sim/operations';
import { ECONOMY } from '../sim/economy';
import { REEF_ISLANDS } from '../sim/reef';
import { patrolHoursAdded } from '../sim/management';
import { yearMoney } from './runway';
import { SURVEY_DAYS } from './goals';

/**
 * A result: what the conservancy's work brought home (M320, the casual year's H2, the main session's playtest: "Every
 * result is a shrug", the first cleanup's tonnes told only on Lānaʻi's page and an advance run past them). Every 'good'
 * event is one, each told once: a survey or a cleanup home, a rule in force, the year's grant, a chapter closed, a boat
 * in the water, an island's volunteers at work; and the annual review whatever it awards. GAME CHOICE: each stops the
 * clock and the advance, and its card opens over the game, as the month's summary does (M289).
 */
export const isResult = (e: GameEvent): boolean => e.severity === 'good' || /annual review$/.test(e.title);

/**
 * The trips the events tell of, each its own: the finished jobs of its title home on its day, taken in order (M320's
 * review: two cleanups home the same day, from the second boat, had both been told as the last one's island).
 */
function tripsOf(world: World, events: readonly GameEvent[]): Array<FinishedJob | undefined> {
  const taken = new Set<FinishedJob>();
  return events.map((e) => {
    const f = world.operations.finished.find((x) => x.title === e.title && x.endDay === world.startEpochDay + e.day && !taken.has(x));
    if (f) taken.add(f);
    return f;
  });
}

/** One result in plain words: a cleanup's tonnes and what still lies there; a survey's findings page itself; else its title and detail. */
function resultSection(world: World, e: GameEvent, f: FinishedJob | undefined): string {
  if (f && isCleanupHome(f) && f.island) {
    const name = ISLAND_NAMES[f.island];
    const took = f.tonnes ?? 0;
    const left = shoreDebrisT(world, f.island);
    return `<section class="finding result-cleanup"><p class="headline"><strong>${took.toFixed(1)} t of derelict nets off ${escapeHtml(name)}'s shore.</strong></p>
      <p class="next-step">About ${Math.round(left)} t still lies there: nets wash ashore all year, so a shore is cleared trip by trip.</p>${seeItHtml('cleanup')}</section>`;
  }
  // Where its counts are seen: Explore's recent counts hold a whole reef's survey's, not one island's (its review).
  if (f && isSurveyHome(f)) return `<section class="result-survey">${areaFindingsHtml(world, f, stepFromFindings(world))}${f.island === null ? seeItHtml('survey') : ''}</section>`;
  if (e.title === GRANT_TITLE) return grantSection(world);
  // The year's review: its verdict, each question and its grade, on the card itself (M321, the casual year's H3: "the
  // year's verdict never came"), the review's page a press away.
  const held = /annual review$/.test(e.title) ? world.campaign.reviews.at(-1) : undefined;
  if (held) {
    const lines = held.lines.map((l) => `<li class="tone-${l.grade === 'good' ? 'good' : l.grade === 'poor' ? 'bad' : 'warn'}"><strong>${escapeHtml(l.label)}:</strong> ${escapeHtml(l.text)}</li>`).join('');
    return `<section class="finding"><p class="headline"><strong>${escapeHtml(plainTitle(e.detail.replace(/ Open Manage to read it\.$/, '')))}</strong></p><ul class="health-facts">${lines}</ul>
      <button class="link" data-act="open" data-kind="review">The year's review</button></section>`;
  }
  // A chapter opened: its first step and the way to it (M321).
  // Its own chapter's step only (its review: two opened the same day had both named the later's), and none with the
  // guide put away (the goals lead then).
  const n = Number(/^Chapter (\d+): /.exec(e.title)?.[1] ?? 0);
  const at = n > 0 && !guideSkipped(world.campaign) ? currentObjective(world) : null;
  const now = at && CHAPTERS.indexOf(at.chapter) === n - 1 ? at : null;
  const first = now?.objective.action ? `<p class="next-step">First: ${escapeHtml(now.objective.title)}.</p><button data-act="goal" data-goal="${escapeHtml(now.objective.action.act)}">${escapeHtml(now.objective.action.label)}</button>` : '';
  return `<section class="finding"><p class="headline"><strong>${escapeHtml(plainTitle(e.title))}</strong></p><p class="next-step">${escapeHtml(plainTitle(e.detail))}</p>${first}</section>`;
}

/** The grant's event's title (`stepOperations`). */
const GRANT_TITLE = 'The year\'s operating grant';

/**
 * The year's grant and what it buys (M324, the casual year's H6, the main session's playtest: "the grant took the funds
 * from $211,750 (7 months) to $931,750 (33 months), unsaid, and every cost the game explains so carefully stopped
 * binding"): the fixed costs it pays to the next grant, what is left for the year's work (`yearMoney`), and what that
 * buys, each from the same money. GAME CHOICE: the grant's money is the year's budget, said so; the core's accounts are
 * unchanged (the funds carry over, as they did), so no figure of the world's moves.
 */
function grantSection(world: World): string {
  const y = yearMoney(world);
  const r = recurringPerMonth(world);
  const ops = world.operations;
  const owed = `the fixed costs to the next grant on ${dateLabel(y.grantDay)} (about ${dollars(y.fixed)}, ${dollars(r.total)} a month as committed today)${y.planned > 0 ? ` and the trips planned (${dollars(y.planned)})` : ''}`;
  const what = y.spare > 0 ? `After ${owed}, ${dollars(y.spare)} is left for the year's work.`
    : `${capitalized(owed)} come to ${dollars(-y.spare)} more than the funds: the year's work waits on a cost cut.`;
  // The year's choices at their prices, each from that money: an island's patrols for a year at their first level (the
  // officers' hours, `patrolHoursAdded`, at `ECONOMY.officerHour`), the second boat while none is bought, a survey.
  const patrolYear = Math.min(...REEF_ISLANDS.map((island) => Math.round(patrolHoursAdded(island, 1) * ECONOMY.officerHour)));
  const boat = ops.boats < 2 && !ops.projects.some((p) => p.kind === 'boat');
  const buys = [
    `A year of patrols along one stretch of an island's coast: ${dollars(patrolYear)}.`,
    ...(boat ? [`The ${ECONOMY.secondBoat.name}, a second boat, so two surveys are at sea at once: ${dollars(ECONOMY.secondBoat.price)}.`] : []),
    `A coral or fish survey, ${SURVEY_DAYS} days at sea: ${dollars(surveyCost('reefBenthic', SURVEY_DAYS))}.`,
  ];
  return `<section class="finding result-grant"><p class="headline"><strong>${escapeHtml(dollars(ECONOMY.operatingGrantPerYear))} from the State for the year.</strong></p>
    <p class="next-step">${escapeHtml(what)}</p>${y.spare > 0 ? `<p class="next-step">What it buys, each from the same ${escapeHtml(dollars(y.spare))}:</p><ul class="health-facts">${buys.map((b) => `<li>${escapeHtml(b)}</li>`).join('')}</ul>` : ''}
    <button class="link" data-act="open" data-kind="operations">Staff and accounts</button></section>`;
}

const capitalized = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);

/**
 * The result card: each result of the day, then Continue. With the month's summary waiting under it (`monthWaits`), its
 * Continue alone, the summary said to follow (M325's review: a way pressed on it, the chapter's first step or a page,
 * had opened over the summary and dropped it, its decision unseen and the clock it held unreturned).
 */
export function resultHtml(world: World, events: readonly GameEvent[], monthWaits = false): string {
  const html = resultCard(world, events);
  if (!monthWaits) return html;
  // Its ways taken off, the step's "Next:" with them, and the summary told at its Continue (M336's review: the step had
  // stood without its button, the summary's line in the report's middle).
  const bare = html.replace(/<p class="steer">[\s\S]*?<\/p>/g, '').replace(/<button(?![^>]*data-act="result-continue")[^>]*>[\s\S]*?<\/button>/g, '').replace(/<p class="month-ways">\s*<\/p>/g, '');
  return bare.replace(/<p class="month-ways">(?=\s*<button[^>]*data-act="result-continue")/, '<p class="next-step">The month\'s summary follows.</p><p class="month-ways">');
}

function resultCard(world: World, events: readonly GameEvent[]): string {
  if (events.length === 0) return '<h3>Nothing new</h3><p class="month-ways"><button class="primary" data-act="result-continue">Continue</button></p>';
  const first = events[0] as GameEvent;
  const trips = tripsOf(world, events);
  const f = trips[0];
  // A survey's findings bring their own heading.
  const heading = f && isSurveyHome(f) ? '' : `<h3>${escapeHtml(f && isCleanupHome(f) ? 'Shore cleanup home' : plainTitle(first.title))}</h3>`;
  return `${heading}${events.map((e, i) => resultSection(world, e, trips[i])).join('')}
    <p class="month-ways"><button class="primary" data-act="result-continue">Continue</button></p>`;
}

/**
 * This calendar year's warm season as it stands (M323, the casual year's H5; its review: the core's own test, the
 * campaign's, judges the opening year alone, so a second year's season was never told): a marine heatwave around the
 * reef under way on 1 July or begun by 31 October (`SEASON`), as the campaign's `seasonTest` reads the log; else, from its
 * last day, the season passed; before either, nothing yet. Keyed by its year, so each year's is told once.
 */
export function seasonKey(world: World): string {
  const s = world.sanctuaries[0];
  if (!s) return '';
  const today = epochDayOf(world);
  const year = civilFromEpochDay(today).year;
  const from = epochDayFromCivil(year, SEASON.fromMonth, 1);
  const end = epochDayFromCivil(year, SEASON.endMonth, SEASON.endDay);
  if (today < from) return '';
  const begun = `A marine heatwave around ${s.site.placeName}`;
  const over = `The marine heatwave around ${s.site.placeName} is over`;
  // Under way on 1 July is read as it stood that day, and stays so (review 34: read as it stood today, a heatwave
  // from June over in July had turned the season's key back to none, and its end told the season passed without one).
  let active = false;
  let checked = false;
  for (const e of world.log) {
    const day = world.startEpochDay + e.day;
    if (day > Math.min(today, end)) break;
    if (!checked && day >= from) {
      checked = true;
      if (active) return `${year}:heatwave`;
    }
    if (e.title === begun) {
      active = true;
      if (day >= from) return `${year}:heatwave`;
    } else if (e.title === over) active = false;
  }
  if (!checked && active) return `${year}:heatwave`;
  return today >= end ? `${year}:season` : '';
}

/** The season's news since `told`: its result where this year's season is newly decided, else none. */
export function seasonNews(world: World, told: string): GameEvent | null {
  const key = seasonKey(world);
  if (key === '' || key === told) return null;
  return seasonResult(world, key.endsWith(':heatwave'));
}

/**
 * The warm season decided, as a result (M323, the casual year's H5: a season without a heatwave had passed unsaid, "a
 * year with no crisis at all"): a marine heatwave come, its toll for a coral survey to find; or the season passed
 * without one, no more claimed than that (its review: no bleaching's absence was measured, and the heat can come later).
 */
function seasonResult(world: World, heatwave: boolean): GameEvent {
  const place = world.sanctuaries[0]?.site.placeName ?? 'the sanctuary';
  return heatwave
    ? { day: world.day, severity: 'good', title: 'The warm season: a marine heatwave', detail: `A marine heatwave has come to ${place} this warm season. Its toll on the coral shows only in a count: survey the coral after the heat.` }
    : { day: world.day, severity: 'good', title: 'The warm season passed', detail: `The warm season passed without a marine heatwave around ${place}. Only a count tells what the season's heat did to the coral.` };
}
